'use client';

import { useEffect, useState } from 'react';
import { useSearchParams } from 'next/navigation';
import { useRouter, usePathname } from 'src/navigation';

import { Filters } from './_filters';
import PaginationControls from 'components/pagination';
import MediaGrid from 'components/perspectives/media-grid';
import noResultsStyles from 'components/perspectives/search/no-results.module.scss';
import { SITE_SEARCH_PER_PAGE } from 'lib/constants.mjs';
import ArticleCard from 'components/cards/article-card';
import type { SanityEssayElasticDocument } from '@metmuseum/types';
import { routeForDocument } from 'lib/routes';
import type { estypes } from '@elastic/elasticsearch';
import { epochTimeToUTCYYYYMMDD } from 'helpers/date-time';
import PageHeader from 'components/headers/page-header';
import SearchBar from 'components/controls/search-bar';
import NoResults from 'components/search/no-results';

export const EssaysAll = ({
	initialResults,
	departmentFilters,
	latestEssays,
}: IEssaysFiltersProps) => {
	const router = useRouter();
	const pathName = usePathname();
	const searchParams = useSearchParams();
	const resultsPerPage = SITE_SEARCH_PER_PAGE;

	const [onlyToah, setOnlyToah] = useState(searchParams.get('toah') || null);

	const [query, setQuery] = useState(searchParams.get('q') || null);

	// Add state for the selected department
	const [selectedDepartment, setSelectedDepartment] = useState(
		searchParams.get('department') || null,
	);

	//add a state to store the fetched media
	const [pageNumber, setPageNumber] = useState(
		searchParams.get('page') ? parseInt(searchParams.get('page')) : 1,
	);
	const totalCount =
		typeof initialResults.hits.total === 'number'
			? initialResults.hits.total
			: initialResults.hits.total.value;

	//when they click a media checkbox, update the selectedMediaType array
	//(which controls the checkboxes, url, and API call)
	const handleCheckboxChange = (e) => {
		const isChecked = e.target.checked;
		if (isChecked) {
			setOnlyToah('true');
		} else {
			setOnlyToah(null);
		}
		setPageNumber(1);
	};

	const handleRemoveSelection = () => {
		setOnlyToah(null);
		setPageNumber(1);
	};

	const handleSearchChange = (e) => {
		const query = e.target.value;
		setQuery(query);
		setPageNumber(1);
	};

	const handlePaginationChange = (e) => {
		const offset = e.currentTarget.value;
		setPageNumber(pageNumber + offset / resultsPerPage);
	};

	//handle writing state to URL
	useEffect(() => {
		const params = new URLSearchParams(searchParams);

		onlyToah ? params.set('toah', 'true') : params.delete('toah');

		selectedDepartment
			? params.set('department', selectedDepartment.toLowerCase())
			: params.delete('department');

		query ? params.set('q', query) : params.delete('q');

		pageNumber !== 1
			? params.set('page', pageNumber.toString())
			: params.delete('page');

		// only call .push on router if params are different; prevents submitting the form twice
		if (params.toString() !== searchParams.toString()) {
			router.push(`${pathName}?${params.toString()}`, {
				scroll: !!params.get('page'),
			});
		}
	}, [
		searchParams,
		onlyToah,
		selectedDepartment,
		pageNumber,
		router,
		pathName,
		query,
	]);

	return (
		<main>
			<PageHeader title="All Essays">
				<SearchBar initialQuery={query} onChange={handleSearchChange} />
			</PageHeader>
			<Filters
				onlyToah={onlyToah}
				handleCheckboxChange={handleCheckboxChange}
				departmentFilters={departmentFilters}
				selectedDepartment={selectedDepartment}
				handleClearAll={() => {
					setPageNumber(1);
					setSelectedDepartment(null);
					setOnlyToah(null);
				}}
				handleClearDepartment={() => {
					setSelectedDepartment(null);
					setPageNumber(1);
				}}
				handleRemoveSelection={handleRemoveSelection}
				handleDepartmentSelectChange={(selectedOption) => {
					setSelectedDepartment(
						// @ts-expect-error TODO: fix typescript hell with react-select and prop drilling
						selectedOption ? selectedOption?.value.toLowerCase() : null,
					);
					setPageNumber(1);
				}}
			/>

			{totalCount > 0 && (
				<section className="productive-component">
					<MediaGrid>
						{initialResults.hits.hits.map(
							({ _source }: { _source: SanityEssayElasticDocument }) => (
								<ArticleCard
									contentType={'essay'}
									image={_source.cardImage}
									contributors={_source?.contributors?.map((contributor) => ({
										person: contributor,
									}))}
									date={epochTimeToUTCYYYYMMDD(_source.publicationDate)}
									key={_source.sanityId}
									link={routeForDocument({
										_type: 'essay',
										slug: { current: _source.slug },
									})}
									sideBySide={true}
									textDescription={_source.description || _source.subtitle}
									title={_source.title}
								/>
							),
						)}
					</MediaGrid>
				</section>
			)}

			{totalCount < 1 && (
				<div key="latestEssays">
					<section className={noResultsStyles.container}>
						<NoResults
							title={`No Results Found for ${query}`}
							body="We couldn't find any matches. Try checking your spelling, using different key words, or broadening your search."
						/>
					</section>
					<section className="productive-component">
						<h2 style={{ marginBottom: 'calc(16px + 0.5vw)' }}>
							Recently Published
						</h2>
						<MediaGrid>
							{latestEssays.hits.hits.map(
								({ _source }: { _source: SanityEssayElasticDocument }) => (
									<ArticleCard
										contentType={'article'}
										image={_source.cardImage}
										contributors={_source?.contributors?.map((contributor) => ({
											person: contributor,
										}))}
										date={epochTimeToUTCYYYYMMDD(_source.publicationDate)}
										key={_source.sanityId}
										link={routeForDocument({
											_type: 'essay',
											slug: { current: _source.slug },
										})}
										sideBySide={true}
										textDescription={_source.description || _source.subtitle}
										title={_source.title}
									/>
								),
							)}
						</MediaGrid>
					</section>
				</div>
			)}

			<PaginationControls
				offset={resultsPerPage * (pageNumber - 1)} // this api starts at page 1
				handlePaginationChange={handlePaginationChange}
				perPage={resultsPerPage}
				totalResults={totalCount}
			/>
		</main>
	);
};

interface IEssaysFiltersProps {
	departmentFilters?: any[];
	toah?: boolean;
	initialResults: {
		hits: {
			hits: any[];
			total: number | estypes.SearchTotalHits;
		};
	};
	latestEssays?: {
		hits: { hits: any[] };
	};
}
